import type { ChecklistTimeframe } from "./enums";

const DAY_MS = 86_400_000;

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function daysBetween(start: Date, end: Date): number {
  return Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / DAY_MS);
}

export const TIMEFRAME_ORDER: Record<ChecklistTimeframe, number> = {
  twelvePlus: 0, nineToTwelve: 1, sixToNine: 2, threeToSix: 3, oneToThree: 4,
  oneMonth: 5, weekOf: 6, dayOf: 7, after: 8, custom: 9,
};

/** Days before the wedding by which a phase's work should be done; null = custom. */
export function deadlineDaysBefore(t: ChecklistTimeframe): number | null {
  switch (t) {
    case "twelvePlus": return 366;
    case "nineToTwelve": return 271;
    case "sixToNine": return 181;
    case "threeToSix": return 91;
    case "oneToThree": return 31;
    case "oneMonth": return 8;
    case "weekOf": return 1;
    case "dayOf": return 0;
    case "after": return -30;
    case "custom": return null;
  }
}

export function currentTimeframe(daysUntil: number): ChecklistTimeframe {
  if (daysUntil <= 0) return "dayOf";
  if (daysUntil <= 7) return "weekOf";
  if (daysUntil <= 30) return "oneMonth";
  if (daysUntil <= 90) return "oneToThree";
  if (daysUntil <= 180) return "threeToSix";
  if (daysUntil <= 270) return "sixToNine";
  if (daysUntil <= 365) return "nineToTwelve";
  return "twelvePlus";
}

/**
 * Due date for a phase. Elapsed phases are staggered forward in phase order
 * instead of being backdated, so a couple planning six months out doesn't
 * open the app to twenty overdue tasks. Same rule as iOS `derivedDueDate`.
 */
export function derivedDueDate(t: ChecklistTimeframe, weddingDate: Date, reference = new Date()): Date | null {
  const offset = deadlineDaysBefore(t);
  if (offset === null) return null;
  const weddingDay = startOfDay(weddingDate);
  const raw = addDays(weddingDay, -offset);
  const staggered = addDays(startOfDay(reference), TIMEFRAME_ORDER[t]);
  const capped = staggered < weddingDay ? staggered : weddingDay;
  return raw > capped ? raw : capped;
}
